import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Menu} from "primereact/menu";
import type {MenuItem} from "primereact/menuitem";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {useRef, useState} from "react";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {useCheckPermission, useIsRevisionReadOnly} from "../../hooks/permissionHooks";
import {useUnloadValuation} from "../../hooks/valuationLoadHooks";
import {selectUsername} from "../../redux/authSlice";
import {
    selectPurposePolicy,
    selectRetentionExpiresAt,
    selectRevaId,
    selectValuationMetadata,
} from "../../redux/persistenceSlice";
import {changeValuationPurpose, deleteValuation} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {formatDate} from "../../util/dateUtil";
import {errorDetailFromErrorI18n} from "../../util/errorUtil";
import {isDeletableLifeCycleState, PERMISSION_RULE_DELETION} from "../../util/permissions";
import {PURPOSE_PERMISSION_RULES} from "../../util/purposeResolution";

/** Startseiten-Reiter, auf dem es nach dem Löschen einer Research-Abfrage weitergeht (#19481). */
const AFTER_DELETE_ROUTE = "/start/research";

/**
 * #19481: Dezenter, neutraler Chip „**Research** · Löschung am …" im Header, mittig im freien Bereich rechts neben
 * Adresse/Objektart/ID (siehe Header.tsx) — auf jeder Seite sichtbar, ohne den Header höher zu machen. Research ist ein
 * regulärer Zustand, daher keine Warn-Optik; die Einschränkungen stehen im Tooltip. Bei schmalem Fenster bleiben nur
 * Icon und „Research".
 *
 * Der ganze Chip öffnet ein Menü mit
 * - „Löschen" (Bestätigungsdialog, danach Research-Reiter): für den Owner, wenn der Zweck es erlaubt
 *   (`ownerMayDelete`), sonst mit `admins`/`reva-lifecycle-deletion`, jeweils nur vor dem vorläufigen Abschluss —
 *   Spiegel von `LifeCycleGuards.checkDeleteAllowed`;
 * - „In Bewertung umwandeln" (Bestätigungsdialog) nur mit dem Recht des Zielzwecks. Die Antwort ersetzt die Metadaten
 *   im Store, danach verschwindet der Chip und die Ansicht zeigt die Möglichkeiten einer Bewertung.
 * Ohne erlaubte Aktion ist der Chip nur ein Hinweis. Bei Zwecken ohne Löschfrist (Bewertung) wird nichts angezeigt.
 */
export default function PurposeElement() {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();
    const unloadValuation = useUnloadValuation();
    const toast = useRef<Toast>(null);
    const menu = useRef<Menu>(null);

    const revaId = useRevaSelector(selectRevaId);
    const metadata = useRevaSelector(selectValuationMetadata);
    const {hasRetention, purposeChangeTarget, ownerMayDelete} = useRevaSelector(selectPurposePolicy);
    const retentionExpiresAt = useRevaSelector(selectRetentionExpiresAt);
    const username = useRevaSelector(selectUsername);
    const mayChange = useCheckPermission(purposeChangeTarget ? PURPOSE_PERMISSION_RULES[purposeChangeTarget] : []);
    const hasDeletionPermission = useCheckPermission(PERMISSION_RULE_DELETION);
    const isRevisionReadOnly = useIsRevisionReadOnly();

    const [changeDialogVisible, setChangeDialogVisible] = useState(false);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    // Der Toast bleibt auch ohne Chip gerendert, damit die Erfolgsmeldung das Löschen (Chip verschwindet) überlebt.
    const toastElement = <Toast ref={toast} appendTo={document.body} />;
    if (!revaId || !metadata || !hasRetention) return toastElement;

    const date = formatDate(retentionExpiresAt, language);
    const deletionText = date ? t("purpose.researchDeletion", {date}) : t("purpose.researchDeletionNoDate");
    const tooltip = `${date ? t("purpose.researchTooltip", {date}) : t("purpose.researchTooltipNoDate")} ${t("purpose.researchHintDetail")}`;
    const canChange = !!purposeChangeTarget && mayChange;
    const isOwner = !!username && metadata.owner === username;
    const canDelete =
        isDeletableLifeCycleState(metadata.lifeCycleState) &&
        (hasDeletionPermission || (ownerMayDelete && isOwner && !isRevisionReadOnly));

    const menuItems: MenuItem[] = [
        ...(canDelete
            ? [
                  {
                      id: "purposeMenuDelete",
                      label: t("purpose.menu.delete"),
                      icon: PrimeIcons.TRASH,
                      command: () => setDeleteDialogVisible(true),
                  },
              ]
            : []),
        ...(canChange
            ? [
                  {
                      id: "purposeMenuChangePurpose",
                      label: t("purpose.change.button"),
                      icon: "pi pi-file-edit",
                      command: () => setChangeDialogVisible(true),
                  },
              ]
            : []),
    ];
    const hasMenu = menuItems.length > 0;

    const showError = (summaryKey: string, err: unknown) =>
        toast.current?.show({
            severity: "error",
            summary: t(summaryKey),
            detail: errorDetailFromErrorI18n(err, language) ?? t("general.error.unspecific"),
            life: 8000,
        });

    const onConfirmChange = async () => {
        if (!purposeChangeTarget) return;
        setBusy(true);
        try {
            await dispatch(changeValuationPurpose(revaId, purposeChangeTarget));
            setChangeDialogVisible(false);
            toast.current?.show({severity: "success", detail: t("purpose.change.success"), life: 5000});
        } catch (err) {
            showError("purpose.change.error", err);
        } finally {
            setBusy(false);
        }
    };

    // Derselbe Lösch-Weg wie in der Bewertungsliste (POST …/delete, storniert serverseitig die REVA-Order).
    // Value-Orders kann eine Research-Abfrage nicht haben (keine Weitergabe, Zweckwechsel nur Richtung Bewertung).
    const onConfirmDelete = async () => {
        setBusy(true);
        try {
            const result = await dispatch(deleteValuation(revaId));
            if (result?.revaId == null) {
                showError("purpose.delete.error", null);
                return;
            }
            setDeleteDialogVisible(false);
            // Erst entladen, sonst leitet die Startseite die noch geladene REVA-ID gleich wieder auf ihre Einstiegsseite.
            await unloadValuation(AFTER_DELETE_ROUTE);
            toast.current?.show({
                severity: "success",
                detail: t("purpose.delete.success", {revaId: result.revaId}),
                life: 5000,
            });
        } finally {
            setBusy(false);
        }
    };

    const dialogFooter = (onCancel: () => void, confirmId: string, confirmLabel: string, onConfirm: () => void) => (
        <div>
            <Button label={t("purpose.change.cancel")} className="p-button-text" disabled={busy} onClick={onCancel} />
            <Button id={confirmId} label={confirmLabel} className="ml-2" autoFocus loading={busy} onClick={onConfirm} />
        </div>
    );

    const content = (
        <>
            <span className={PrimeIcons.SEARCH} />
            <span className="HeaderPurposeHintText whitespace-nowrap overflow-hidden text-ellipsis">
                <strong>{t("purpose.name.RESEARCH")}</strong>
                <span className="HeaderPurposeHintDeletion"> · {deletionText}</span>
            </span>
        </>
    );

    return (
        <>
            {/* im Body, damit der Toast keinen Platz in der Flex-Zeile des Headers belegt */}
            {toastElement}
            <div className="HeaderPurposeSlot">
                {hasMenu ? (
                    <button
                        type="button"
                        id="header-purpose-element"
                        className="HeaderPurposeHint HeaderPurposeHintInteractive flex items-center gap-2 min-w-0"
                        data-pr-tooltip={tooltip}
                        aria-label={`${tooltip} ${t("purpose.menu.label")}`}
                        aria-haspopup="menu"
                        aria-controls="purposeMenu"
                        aria-expanded={menuOpen}
                        onClick={(event) => menu.current?.toggle(event)}
                    >
                        {content}
                        <span className="HeaderPurposeHintCaret pi pi-angle-down" />
                    </button>
                ) : (
                    <div
                        id="header-purpose-element"
                        role="status"
                        aria-label={tooltip}
                        className="HeaderPurposeHint flex items-center gap-2 min-w-0"
                        data-pr-tooltip={tooltip}
                    >
                        {content}
                    </div>
                )}
                <Tooltip target=".HeaderPurposeHint" position="bottom" />
            </div>
            {hasMenu && (
                <Menu
                    id="purposeMenu"
                    ref={menu}
                    model={menuItems}
                    popup
                    onShow={() => setMenuOpen(true)}
                    onHide={() => setMenuOpen(false)}
                />
            )}
            <Dialog
                visible={changeDialogVisible}
                onHide={() => !busy && setChangeDialogVisible(false)}
                footer={dialogFooter(
                    () => setChangeDialogVisible(false),
                    "changePurposeConfirmButton",
                    t("purpose.change.confirm"),
                    () => void onConfirmChange(),
                )}
                header={<span className="font-semibold">{t("purpose.change.dialogHeader")}</span>}
                style={{maxWidth: "40rem"}}
            >
                <p className="mt-0">{t("purpose.change.dialogIntro", {revaId})}</p>
                <p className="mb-0">{t("purpose.change.explanation")}</p>
            </Dialog>
            <Dialog
                visible={deleteDialogVisible}
                onHide={() => !busy && setDeleteDialogVisible(false)}
                footer={dialogFooter(
                    () => setDeleteDialogVisible(false),
                    "purposeDeleteConfirmButton",
                    t("purpose.delete.confirm"),
                    () => void onConfirmDelete(),
                )}
                header={<span className="font-semibold">{t("purpose.delete.dialogHeader")}</span>}
                style={{maxWidth: "40rem"}}
            >
                <p className="my-0">{t("purpose.delete.dialogIntro", {revaId})}</p>
            </Dialog>
        </>
    );
}
